import { useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { createRole } from 'features/management/api/createRole';
import { updateRole } from 'features/management/api/updateRole';
import { getErrorMessage } from 'shared/utils/errorMessage';
import { showErrorToastWithRetry } from 'shared/utils/showErrorToastWithRetry';
import { managementKeys } from '../../api/queryKeys';

/**
 * Mutation hook for creating or updating a role.
 * If `id` is provided, runs update; otherwise create.
 * Retries on 5xx up to 3 times with 3s delay.
 *
 * @param {{ id?: string | number, onSuccess?: () => void }} options
 * @returns {{ upsertMutation: { mutate: (payload: object) => void, isLoading: boolean } }}
 */
const useUpsertRole = ({ id, onSuccess } = {}) => {
  const queryClient = useQueryClient();
  const lastPayloadRef = useRef(null);

  const upsertMutation = useMutation(
    (payload) => {
      lastPayloadRef.current = payload;
      return id ? updateRole(id, payload) : createRole(payload);
    },
    {
      retry: (failureCount, error) => {
        const status = error?.status || error?.response?.status || 500;
        return status >= 500 && failureCount < 3;
      },
      retryDelay: 3000,
      onSuccess: (res) => {
        toast.success(res?.message || (id ? 'Role updated' : 'Role created'));
        queryClient.invalidateQueries(managementKeys.roles());
        onSuccess?.(res);
      },
      onError: (error) => {
        const status = error?.status || error?.response?.status || 500;
        const message = getErrorMessage(error);
        const label = id ? 'Update role' : 'Add role';

        if (status >= 500) {
          showErrorToastWithRetry(`${label}: ${message}`, () =>
            upsertMutation.mutate(lastPayloadRef.current)
          );
        } else {
          toast.error(`${label}: ${message}`);
        }
      },
    }
  );

  return { upsertMutation };
};

export default useUpsertRole;
